import {
  Badge,
  Button,
  Group,
  Loader,
  Menu,
  Modal,
  NumberInput,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconArchive,
  IconCalendarPlus,
  IconCheck,
  IconClipboardList,
  IconDotsVertical,
  IconPlus,
  IconSend,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useMemo, useState } from "react";
import { usePermission } from "@how/app-kit";
import {
  ConfirmActionModal,
  EmptyState,
  PageHeader,
  useFocusWhenReady,
  useSearchParamState,
} from "@how/ui";
import { ArchivedSeasonsModal } from "./ArchivedSeasonsModal";
import { AssignModal, type AssignModalProps } from "./AssignModal";
import { PlanModal } from "./PlanModal";
import { PositionsModal } from "./PositionsModal";
import { SeasonModal } from "./SeasonModal";
import {
  BUSY_ELSEWHERE,
  SEASON_STATUS_LABEL,
  defaultSeason,
  deleteRefusal,
  formatPlanWhen,
  formatSeasonDates,
  gridRefusal,
  todayIso,
  useArchiveSeason,
  useCancelPlan,
  useCloseSeason,
  useDeleteSeason,
  useRemovePlanPosition,
  useRespondFor,
  useSeasonBusyElsewhere,
  useSeasonGrid,
  useSeasons,
  useSendRequests,
  useServePositions,
  useSetPlanPosition,
  useUnassignPerson,
  type GridAssignment,
  type GridPlan,
  type SeasonGrid,
} from "./services-queries";
import "./services.css";
import { semanticColor, type SemanticColor } from "@how/design-system";

const RESPONSE_COLOR: Record<GridAssignment["response"], SemanticColor> = {
  no_reply: semanticColor("neutral"),
  accepted: semanticColor("success"),
  declined: semanticColor("danger"),
};

/** One person in a cell: their answer as colour and icon, a conflict as an orange dot, and the staff menu. */
function Chip({
  assignment,
  canManage,
  busyElsewhere,
  onUnassign,
  onRespond,
}: {
  assignment: GridAssignment;
  canManage: boolean;
  busyElsewhere: boolean;
  onUnassign: () => void;
  onRespond: (accepted: boolean) => void;
}) {
  const name = `${assignment.firstName} ${assignment.lastName}`;
  const title =
    assignment.response === "accepted"
      ? "Accepted"
      : assignment.response === "declined"
        ? `Declined${assignment.note === null ? "" : `: ${assignment.note}`}`
        : assignment.requested
          ? "Asked, no reply yet"
          : "Not asked yet";
  const fullTitle = busyElsewhere ? `${title} · ${BUSY_ELSEWHERE}` : title;
  const chip = (
    <Badge
      variant="light"
      color={RESPONSE_COLOR[assignment.response]}
      className="sched-chip"
      leftSection={
        assignment.response === "accepted" ? (
          <IconCheck size={12} />
        ) : assignment.response === "declined" ? (
          <IconX size={12} />
        ) : null
      }
      rightSection={
        assignment.conflict || busyElsewhere ? (
          <>
            {assignment.conflict ? (
              <span
                className="sched-conflict"
                role="img"
                aria-label="Blockout on this date"
                title="Blockout on this date"
              />
            ) : null}
            {busyElsewhere ? (
              <span
                className="sched-busy"
                role="img"
                aria-label={BUSY_ELSEWHERE}
                title={BUSY_ELSEWHERE}
              />
            ) : null}
          </>
        ) : null
      }
    >
      {name}
    </Badge>
  );
  if (!canManage) return <Tooltip label={fullTitle}>{chip}</Tooltip>;
  return (
    <Menu shadow="md" position="bottom-start">
      <Menu.Target>
        <UnstyledButton aria-label={`${name}: ${fullTitle}`} className="sched-chip-button">
          {chip}
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>{fullTitle}</Menu.Label>
        <Menu.Item
          leftSection={<IconCheck size={14} />}
          onClick={() => {
            onRespond(true);
          }}
        >
          Mark accepted
        </Menu.Item>
        <Menu.Item
          leftSection={<IconX size={14} />}
          onClick={() => {
            onRespond(false);
          }}
        >
          Mark declined
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item color="red" onClick={onUnassign}>
          Take off this date
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

/** Rows of the grid: every position that appears on any plan, or that exists on a team, grouped by team. */
function useRows(grid: SeasonGrid | undefined) {
  const positions = useServePositions();
  return useMemo(() => {
    const seen = new Map<
      string,
      { positionId: string; position: string; team: string; teamId: string }
    >();
    for (const p of positions.data ?? []) {
      if (p.isActive)
        seen.set(p.id, { positionId: p.id, position: p.name, team: p.teamName, teamId: p.teamId });
    }
    for (const plan of grid?.plans ?? []) {
      for (const slot of plan.positions) {
        if (!seen.has(slot.positionId)) {
          const teamId = (positions.data ?? []).find((p) => p.id === slot.positionId)?.teamId ?? "";
          seen.set(slot.positionId, {
            positionId: slot.positionId,
            position: slot.position,
            team: slot.team,
            teamId,
          });
        }
      }
    }
    return [...seen.values()].sort(
      (a, b) => a.team.localeCompare(b.team) || a.position.localeCompare(b.position),
    );
  }, [grid, positions.data]);
}

/**
 * The grid (ADR 0043 §5, task 12): plans across, positions down, the people in each cell with their answer and any
 * blockout conflict, and on the right who is on how often. A season's requests go out in one action.
 */
export function ServicesPage() {
  const canManage = usePermission("services.manage", { write: true });
  const manage = canManage.data === true;
  const seasons = useSeasons();
  const [seasonParam, setSeasonParam] = useSearchParamState("season", "");
  const allSeasons = seasons.data ?? [];
  // A season named in the address opens even when archived; otherwise (or once it is deleted) the current or next.
  const seasonId = seasons.isSuccess
    ? allSeasons.some((s) => s.id === seasonParam)
      ? seasonParam
      : (defaultSeason(allSeasons, todayIso())?.id ?? null)
    : seasonParam === ""
      ? null
      : seasonParam;
  const grid = useSeasonGrid(seasonId);
  const busyElsewhere = useSeasonBusyElsewhere(seasonId, canManage.data === true);
  const rows = useRows(grid.data);
  const season = allSeasons.find((s) => s.id === seasonId);
  const archived = season !== undefined && season.archivedAt !== null;
  const archivedSeasons = allSeasons.filter((s) => s.archivedAt !== null);
  // An archived season is history: its grid is read, not changed, until it is restored.
  const editable = manage && !archived;

  const [addingSeason, setAddingSeason] = useState(false);
  const [addingPlan, setAddingPlan] = useState(false);
  const [positionsOpen, setPositionsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");
  const [closing, setClosing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const focusWhenReady = useFocusWhenReady();
  const [needFor, setNeedFor] = useState<{
    plan: GridPlan;
    positionId: string;
    position: string;
  } | null>(null);
  const [needCount, setNeedCount] = useState(1);
  /** Why the database refused the last Save in the "how many" dialog, in words (#403). */
  const [needError, setNeedError] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<AssignModalProps["slot"]>(undefined);

  const sendRequests = useSendRequests();
  const closeSeason = useCloseSeason();
  const archiveSeason = useArchiveSeason();
  const deleteSeason = useDeleteSeason();
  const setPlanPosition = useSetPlanPosition();
  const removePlanPosition = useRemovePlanPosition();
  const cancelPlan = useCancelPlan();
  const unassign = useUnassignPerson();
  const respond = useRespondFor();

  const plans = grid.data?.plans ?? [];
  const unasked = new Set(
    plans.flatMap((p) =>
      p.positions.flatMap((s) => s.assignments.filter((a) => !a.requested).map((a) => a.personId)),
    ),
  ).size;
  const often = Math.max(1, Math.ceil(plans.filter((p) => !p.isCancelled).length / 2));
  // Deleting is for a season made by mistake: nobody asked, nobody answered (the database checks it again).
  const deletable =
    season !== undefined &&
    season.requestedAt === null &&
    grid.data !== undefined &&
    !plans.some((p) =>
      p.positions.some((s) => s.assignments.some((a) => a.response !== "no_reply")),
    );

  /** Says a refused grid action in words (#403), so nothing fails silently. */
  const refused = (what: string) => (error: unknown) => {
    notifications.show({ color: semanticColor("danger"), message: gridRefusal(what, error) });
  };

  const restore = (id: string, name: string) => {
    if (archiveSeason.isPending) return;
    archiveSeason.mutate(
      { seasonId: id, archived: false },
      {
        onSuccess: () => {
          setSeasonParam(id);
          notifications.show({ color: semanticColor("success"), message: `${name} restored` });
        },
        onError: () => {
          notifications.show({
            color: semanticColor("danger"),
            message: `${name} was not restored. Check your access and try again.`,
          });
        },
      },
    );
  };

  const actions = (
    <>
      <Select
        aria-label="Season"
        placeholder={allSeasons.length === 0 ? "No seasons yet" : "Season"}
        data={allSeasons
          .filter((s) => s.archivedAt === null || s.id === seasonId)
          .map((s) => ({
            value: s.id,
            label: s.archivedAt === null ? s.name : `${s.name} (archived)`,
          }))}
        value={seasonId}
        allowDeselect={false}
        onChange={(value) => {
          setSeasonParam(value ?? undefined);
        }}
        w={200}
      />
      <Button
        variant="light"
        leftSection={<IconClipboardList size={16} />}
        onClick={() => {
          setPositionsOpen(true);
        }}
      >
        Positions
      </Button>
      {manage ? (
        <>
          <Button
            variant="light"
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setAddingSeason(true);
            }}
          >
            Add season
          </Button>
          {season !== undefined && season.status !== "closed" && !archived ? (
            <Button
              variant="light"
              leftSection={<IconCalendarPlus size={16} />}
              onClick={() => {
                setAddingPlan(true);
              }}
            >
              Add plan
            </Button>
          ) : null}
          {season !== undefined && season.status !== "closed" && !archived ? (
            <Button
              leftSection={<IconSend size={16} />}
              disabled={unasked === 0}
              onClick={() => {
                setSending(true);
              }}
            >
              Send requests{unasked > 0 ? ` (${String(unasked)})` : ""}
            </Button>
          ) : null}
        </>
      ) : null}
    </>
  );

  return (
    <>
      <PageHeader
        title="Services"
        description="Who serves when. One request per person for the whole season; they answer each date."
        actions={actions}
      />
      {seasons.isSuccess && allSeasons.length === 0 ? (
        <EmptyState
          title="No seasons yet"
          description="A season is the window you ask volunteers for: add one, add its dates, then send everyone one request."
          action={
            manage ? (
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => {
                  setAddingSeason(true);
                }}
              >
                Add season
              </Button>
            ) : undefined
          }
        />
      ) : null}
      {seasons.isSuccess && allSeasons.length > 0 && seasonId === null ? (
        <EmptyState
          title="Every season is archived"
          description="Add the next season, or open an archived one to read it or restore it."
          action={
            <Button
              variant="light"
              leftSection={<IconArchive size={16} />}
              onClick={() => {
                setArchivedOpen(true);
              }}
            >
              Archived seasons ({String(archivedSeasons.length)})
            </Button>
          }
        />
      ) : null}
      {season !== undefined ? (
        <Group gap="sm" mb="sm">
          <Badge
            variant="light"
            color={
              season.status === "requested"
                ? semanticColor("success")
                : season.status === "closed"
                  ? semanticColor("neutral")
                  : semanticColor("info")
            }
          >
            {SEASON_STATUS_LABEL[season.status]}
          </Badge>
          {archived ? (
            <Badge variant="outline" color={semanticColor("neutral")}>
              Archived
            </Badge>
          ) : null}
          <Text size="sm" c="dimmed">
            {formatSeasonDates(season.startsOn, season.endsOn)}
            {" · "}
            {String(plans.length)} {plans.length === 1 ? "plan" : "plans"}
          </Text>
          {manage && season.status !== "closed" && !archived ? (
            <Button
              variant="subtle"
              size="compact-sm"
              color={semanticColor("neutral")}
              onClick={() => {
                setClosing(true);
              }}
            >
              Close season
            </Button>
          ) : null}
          {manage && !archived ? (
            <Button
              variant="subtle"
              size="compact-sm"
              color={semanticColor("neutral")}
              data-season-action="archive"
              onClick={() => {
                setArchiving(true);
              }}
            >
              Archive season
            </Button>
          ) : null}
          {manage && archived ? (
            <Button
              variant="subtle"
              size="compact-sm"
              data-season-action="restore"
              leftSection={archiveSeason.isPending ? <Loader size={12} /> : null}
              onClick={() => {
                restore(season.id, season.name);
                focusWhenReady('[data-season-action="archive"]');
              }}
            >
              Restore season
            </Button>
          ) : null}
          {manage && deletable ? (
            <Button
              variant="subtle"
              size="compact-sm"
              color={semanticColor("danger")}
              leftSection={<IconTrash size={14} />}
              onClick={() => {
                setDeleting(true);
              }}
            >
              Delete season
            </Button>
          ) : null}
          {archivedSeasons.length > 0 ? (
            <Button
              variant="subtle"
              size="compact-sm"
              color={semanticColor("neutral")}
              data-season-action="archived-list"
              leftSection={<IconArchive size={14} />}
              onClick={() => {
                setArchivedOpen(true);
              }}
            >
              Archived seasons ({String(archivedSeasons.length)})
            </Button>
          ) : null}
        </Group>
      ) : null}
      {grid.isError ? (
        <EmptyState
          title="Could not load the schedule."
          action={
            <Button
              variant="light"
              onClick={() => {
                void grid.refetch();
              }}
            >
              Try again
            </Button>
          }
        />
      ) : null}
      {grid.data !== undefined && plans.length === 0 ? (
        <EmptyState
          title="No plans in this season"
          description="Add the dates the season covers; each one lists what it needs and who is on it."
        />
      ) : null}
      {grid.data !== undefined && plans.length > 0 ? (
        <div className="sched-layout">
          <ScrollArea type="auto" className="sched-scroll">
            <Table className="sched-grid" withTableBorder withColumnBorders aria-label="Schedule">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th className="sched-sticky">Position</Table.Th>
                  {plans.map((plan) => (
                    <Table.Th
                      key={plan.id}
                      {...(plan.isCancelled ? { className: "sched-cancelled" } : {})}
                    >
                      <Group gap={4} wrap="nowrap" justify="space-between">
                        <div>
                          <Text
                            size="sm"
                            fw={600}
                            style={{ textTransform: "none", letterSpacing: 0 }}
                          >
                            {formatPlanWhen(plan.startsAt, plan.campusTimeZone)}
                          </Text>
                          <Text
                            size="xs"
                            c="dimmed"
                            style={{ textTransform: "none", letterSpacing: 0 }}
                          >
                            {plan.isCancelled ? "Cancelled" : plan.title}
                            {plan.campus === null ? "" : ` · ${plan.campus}`}
                          </Text>
                        </div>
                        {editable ? (
                          <Menu shadow="md" position="bottom-end">
                            <Menu.Target>
                              <UnstyledButton
                                aria-label={`Plan ${formatPlanWhen(plan.startsAt, plan.campusTimeZone)}`}
                                className="sched-plan-menu"
                              >
                                <IconDotsVertical size={14} />
                              </UnstyledButton>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                {...(plan.isCancelled ? {} : { color: "red" })}
                                onClick={() => {
                                  cancelPlan.mutate(
                                    { planId: plan.id, isCancelled: !plan.isCancelled },
                                    {
                                      onError: refused(
                                        plan.isCancelled
                                          ? "The date was not restored"
                                          : "The date was not cancelled",
                                      ),
                                    },
                                  );
                                }}
                              >
                                {plan.isCancelled ? "Restore this date" : "Cancel this date"}
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        ) : null}
                      </Group>
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row, index) => {
                  const newTeam = index === 0 || rows[index - 1]?.team !== row.team;
                  return (
                    <Table.Tr key={row.positionId} data-position={row.position}>
                      <Table.Td className="sched-sticky">
                        {newTeam ? (
                          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                            {row.team}
                          </Text>
                        ) : null}
                        <Text size="sm" fw={500}>
                          {row.position}
                        </Text>
                      </Table.Td>
                      {plans.map((plan) => {
                        const slot = plan.positions.find((s) => s.positionId === row.positionId);
                        if (slot === undefined) {
                          return (
                            <Table.Td key={plan.id} className="sched-cell sched-empty">
                              {editable && !plan.isCancelled ? (
                                <Button
                                  variant="subtle"
                                  size="compact-xs"
                                  color="gray"
                                  onClick={() => {
                                    setNeedCount(1);
                                    setNeedError(null);
                                    setNeedFor({
                                      plan,
                                      positionId: row.positionId,
                                      position: row.position,
                                    });
                                  }}
                                >
                                  Need
                                </Button>
                              ) : (
                                <Text size="xs" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                          );
                        }
                        const filled = slot.assignments.length + slot.hiddenCount;
                        const short = filled < slot.needCount;
                        return (
                          <Table.Td key={plan.id} className="sched-cell">
                            <Stack gap={4}>
                              <Group gap={4} justify="space-between" wrap="nowrap">
                                <Text
                                  size="xs"
                                  c={short ? "orange" : "dimmed"}
                                  fw={short ? 600 : 400}
                                >
                                  {String(filled)} of {String(slot.needCount)}
                                </Text>
                                {editable && !plan.isCancelled ? (
                                  <Menu shadow="md" position="bottom-end">
                                    <Menu.Target>
                                      <UnstyledButton
                                        aria-label={`${row.position} on ${formatPlanWhen(plan.startsAt, plan.campusTimeZone)}`}
                                        className="sched-plan-menu"
                                      >
                                        <IconDotsVertical size={14} />
                                      </UnstyledButton>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                      <Menu.Item
                                        onClick={() => {
                                          setNeedCount(slot.needCount);
                                          setNeedError(null);
                                          setNeedFor({
                                            plan,
                                            positionId: row.positionId,
                                            position: row.position,
                                          });
                                        }}
                                      >
                                        Change how many
                                      </Menu.Item>
                                      <Menu.Item
                                        color="red"
                                        disabled={filled > 0}
                                        onClick={() => {
                                          removePlanPosition.mutate(slot.planPositionId, {
                                            onError: refused(
                                              `${row.position} was not removed from this date`,
                                            ),
                                          });
                                        }}
                                      >
                                        Not needed this date
                                      </Menu.Item>
                                    </Menu.Dropdown>
                                  </Menu>
                                ) : null}
                              </Group>
                              {slot.assignments.map((a) => (
                                <Chip
                                  key={a.id}
                                  assignment={a}
                                  canManage={editable}
                                  busyElsewhere={busyElsewhere.data?.has(a.id) === true}
                                  onUnassign={() => {
                                    unassign.mutate(a.id, {
                                      onError: refused(
                                        `${a.firstName} ${a.lastName} was not taken off this date`,
                                      ),
                                    });
                                  }}
                                  onRespond={(accepted) => {
                                    respond.mutate(
                                      { assignmentId: a.id, accepted },
                                      {
                                        onError: refused(
                                          `${a.firstName} ${a.lastName} was not marked ${accepted ? "accepted" : "declined"}`,
                                        ),
                                      },
                                    );
                                  }}
                                />
                              ))}
                              {slot.hiddenCount > 0 ? (
                                <Text size="xs" c="dimmed">
                                  {String(slot.hiddenCount)} you cannot see
                                </Text>
                              ) : null}
                              {editable && !plan.isCancelled ? (
                                <Button
                                  variant="subtle"
                                  size="compact-xs"
                                  leftSection={<IconPlus size={12} />}
                                  onClick={() => {
                                    setAssigning({
                                      ...slot,
                                      teamId: row.teamId,
                                      planId: plan.id,
                                      planTitle: plan.title,
                                      planWhen: formatPlanWhen(plan.startsAt, plan.campusTimeZone),
                                    });
                                  }}
                                >
                                  Add
                                </Button>
                              ) : null}
                            </Stack>
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          <aside className="sched-load" aria-label="Who is on how often">
            <Text size="sm" fw={600} mb="xs">
              Who is on how often
            </Text>
            {grid.data.load.length === 0 ? (
              <Text size="sm" c="dimmed">
                Nobody is on a date yet.
              </Text>
            ) : (
              <Table className="sched-load-table" verticalSpacing={4}>
                <Table.Tbody>
                  {grid.data.load.map((l) => (
                    <Table.Tr key={l.personId}>
                      <Table.Td>
                        <Text size="sm">
                          {l.firstName} {l.lastName}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {String(l.accepted)} yes · {String(l.declined)} no
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Group gap={6} justify="flex-end" wrap="nowrap">
                          {l.assignments >= often ? (
                            <Badge variant="light" color="orange" size="sm" className="sched-often">
                              Often
                            </Badge>
                          ) : null}
                          <Text size="sm" fw={600} className="sched-num">
                            {String(l.assignments)}
                          </Text>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            <Text size="xs" c="dimmed" mt="xs">
              Often: on half the season&apos;s dates or more.
            </Text>
          </aside>
        </div>
      ) : null}

      <SeasonModal
        key={addingSeason ? "new" : "closed"}
        season={addingSeason ? null : undefined}
        onClose={() => {
          setAddingSeason(false);
        }}
        onSaved={(id) => {
          setSeasonParam(id);
        }}
      />
      {seasonId !== null ? (
        <PlanModal
          key={addingPlan ? "open" : "closed"}
          opened={addingPlan}
          seasonId={seasonId}
          {...(plans.length > 0
            ? { defaultStartsAt: nextWeekLocal(plans[plans.length - 1]?.startsAt ?? "") }
            : {})}
          onClose={() => {
            setAddingPlan(false);
          }}
        />
      ) : null}
      <PositionsModal
        opened={positionsOpen}
        canManage={manage}
        onClose={() => {
          setPositionsOpen(false);
        }}
      />
      <AssignModal
        key={assigning?.planPositionId ?? "closed"}
        slot={assigning}
        onClose={() => {
          setAssigning(undefined);
        }}
      />
      <Modal
        opened={needFor !== null}
        onClose={() => {
          setNeedFor(null);
        }}
        title={
          needFor === null
            ? ""
            : `${needFor.position} · ${formatPlanWhen(needFor.plan.startsAt, needFor.plan.campusTimeZone)}`
        }
        radius="lg"
      >
        <Stack gap="sm">
          <NumberInput
            label="How many are needed"
            min={0}
            max={100}
            value={needCount}
            onChange={(v) => {
              setNeedCount(typeof v === "number" ? v : 1);
            }}
          />
          {needError === null ? null : (
            <Text size="sm" c="red" role="alert">
              {needError}
            </Text>
          )}
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => {
                setNeedFor(null);
              }}
            >
              Cancel
            </Button>
            <Button
              loading={setPlanPosition.isPending}
              onClick={() => {
                if (needFor === null) return;
                setNeedError(null);
                setPlanPosition.mutate(
                  { planId: needFor.plan.id, positionId: needFor.positionId, needCount },
                  {
                    onSuccess: () => {
                      setNeedFor(null);
                    },
                    onError: (error) => {
                      setNeedError(gridRefusal("Not saved", error));
                    },
                  },
                );
              }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Modal
        opened={sending}
        onClose={() => {
          setSending(false);
        }}
        title="Send the season's requests"
        radius="lg"
      >
        <Stack gap="sm">
          <Text size="sm">
            {String(unasked)} {unasked === 1 ? "person gets" : "people get"} one request for every
            date they are on in {season?.name ?? "this season"}. Someone already asked is asked
            again only for new dates. They answer each date in the portal.
          </Text>
          <Textarea
            label="A note to go with it"
            placeholder="Thank you for serving this season. Say yes or no to each date when you can."
            autosize
            minRows={2}
            value={message}
            onChange={(e) => {
              setMessage(e.currentTarget.value);
            }}
          />
          {sendRequests.isError ? (
            <Text size="sm" c="red">
              The requests were not sent. Check your access and try again.
            </Text>
          ) : null}
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => {
                setSending(false);
              }}
            >
              Cancel
            </Button>
            <Button
              leftSection={<IconSend size={16} />}
              loading={sendRequests.isPending}
              onClick={() => {
                if (seasonId === null) return;
                sendRequests.mutate(
                  { seasonId, message },
                  {
                    onSuccess: () => {
                      setSending(false);
                      setMessage("");
                    },
                  },
                );
              }}
            >
              Send
            </Button>
          </Group>
        </Stack>
      </Modal>
      <ConfirmActionModal
        opened={closing}
        onClose={() => {
          setClosing(false);
        }}
        title="Close this season"
        message={`Close ${season?.name ?? "this season"}? No more requests go out for it; what people answered stays.`}
        confirmLabel="Close season"
        loading={closeSeason.isPending}
        onConfirm={() => {
          if (seasonId === null) return;
          closeSeason.mutate(seasonId, {
            onSuccess: () => {
              setClosing(false);
            },
          });
        }}
      />
      <ConfirmActionModal
        opened={archiving}
        onClose={() => {
          setArchiving(false);
        }}
        title="Archive this season"
        message={`Archive ${season?.name ?? "this season"}? It leaves the season list and volunteers' schedules. Its dates, requests and answers stay, and you can restore it.`}
        confirmLabel="Archive season"
        loading={archiveSeason.isPending}
        onConfirm={() => {
          if (season === undefined) return;
          const { id, name } = season;
          archiveSeason.mutate(
            { seasonId: id, archived: true },
            {
              onSuccess: () => {
                setArchiving(false);
                // Stay on it, now marked archived, with Restore where Archive was.
                setSeasonParam(id);
                focusWhenReady('[data-season-action="restore"]');
                notifications.show({
                  color: semanticColor("success"),
                  message: `${name} archived`,
                });
              },
              onError: () => {
                setArchiving(false);
                notifications.show({
                  color: semanticColor("danger"),
                  message: `${name} was not archived. Check your access and try again.`,
                });
              },
            },
          );
        }}
      />
      <ConfirmActionModal
        opened={deleting}
        onClose={() => {
          setDeleting(false);
        }}
        title="Delete this season"
        message={`Delete ${season?.name ?? "this season"}${
          plans.length === 0
            ? ""
            : ` and its ${String(plans.length)} ${plans.length === 1 ? "plan" : "plans"}`
        }? Nobody was asked or answered, so nobody is told. This cannot be undone.`}
        confirmLabel="Delete season"
        danger
        loading={deleteSeason.isPending}
        onConfirm={() => {
          if (season === undefined) return;
          const { id, name } = season;
          deleteSeason.mutate(id, {
            onSuccess: () => {
              setDeleting(false);
              setSeasonParam(undefined);
              focusWhenReady('input[aria-label="Season"]');
              notifications.show({ color: semanticColor("success"), message: `${name} deleted` });
            },
            onError: (error) => {
              setDeleting(false);
              notifications.show({
                color: semanticColor("danger"),
                message: deleteRefusal(error, name),
              });
            },
          });
        }}
      />
      <ArchivedSeasonsModal
        opened={archivedOpen}
        seasons={archivedSeasons}
        canRestore={manage}
        restoring={archiveSeason.isPending}
        onClose={() => {
          setArchivedOpen(false);
        }}
        onOpen={(id) => {
          setArchivedOpen(false);
          setSeasonParam(id);
          focusWhenReady('input[aria-label="Season"]');
        }}
        onRestore={(id, name) => {
          setArchivedOpen(false);
          restore(id, name);
          focusWhenReady('[data-season-action="archive"]');
        }}
      />
    </>
  );
}

/** A week after a plan, as a datetime-local value in the reader's zone. */
function nextWeekLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + 7);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
